import React, { useState, useMemo } from 'react';
import {
  Text,
  TextInput,
  Tooltip,
  Loader,
  ScrollArea,
  Center,
  Alert,
  Divider,
  Box,
  Group,
  ActionIcon
} from '@mantine/core';
import {
  IconSearch,
  IconAlertCircle,
  IconCommand,
  IconInfoCircle,
  IconMapPin,
  IconSchool,
  IconCoin,
  IconUserOff,
  IconSkull,
  IconBellRinging,
  IconUsersGroup,
  IconDeviceFloppy,
  IconPower,
  IconPaw,
  IconRefresh,
  IconChartBar,
  IconX,
  IconTrophy,
  IconFlame,
  IconArrowUp
} from '@tabler/icons-react';
import { Action } from '../api/actions';
import { useActions } from '../hooks/useActions';

interface ActionsListProps {
  onActionSelect: (action: Action) => void;
  selectedAction: Action | null;
}

/**
 * Actions list component
 * Displays a list of available GM actions
 */
const ActionsList: React.FC<ActionsListProps> = ({ onActionSelect, selectedAction }) => {
  // State for search
  const [search, setSearch] = useState('');
  
  // Reference for scroll area
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  
  // Fetch actions
  const { actions, isLoading, isError } = useActions();
  
  // Action icon mapping
  const getActionIcon = useMemo(() => {
    return (actionId: string) => {
      switch (actionId) {
        case 'warp':
          return <IconMapPin size={20} />;
        case 'learn_all_skill':
          return <IconSchool size={20} />;
        case 'insert_gold':
          return <IconCoin size={20} />;
        case 'kick':
          return <IconUserOff size={20} />;
        case 'kill_target':
          return <IconSkull size={20} />;
        case 'notice':
          return <IconBellRinging size={20} />;
        case 'recall_player':
          return <IconUsersGroup size={20} />;
        case 'saveall':
          return <IconDeviceFloppy size={20} />;
        case 'shutdown':
          return <IconPower size={20} />;
        case 'learn_creature_all_skill':
          return <IconPaw size={20} />;
        case 'reset_summon_skill':
          return <IconRefresh size={20} />;
        case 'stat':
          return <IconChartBar size={20} />;
        case 'add_exp_jp':
          return <IconTrophy size={20} />;
        case 'add_chaos':
          return <IconFlame size={20} />;
        case 'set_player_level':
          return <IconArrowUp size={20} />;
        case 'set_creature_level':
          return <IconArrowUp size={20} />;
        default:
          return <IconCommand size={20} />;
      }
    };
  }, []);
  
  // Filter actions by search
  const filteredActions = actions.filter(action => 
    search.trim() === '' || 
    action.name.toLowerCase().includes(search.toLowerCase()) ||
    action.description.toLowerCase().includes(search.toLowerCase())
  );
  
  // Render action row
  const renderActionRow = (action: Action, isSelected: boolean) => {
    return (
      <Tooltip
        label={action.tooltip}
        position="right"
        withArrow
        multiline
        w={220}
      >
        <div>
          <div 
            className={`p-2 flex items-center cursor-pointer transition-colors ${
              isSelected 
                ? 'bg-blue-100' 
                : 'hover:bg-gray-50'
            }`}
            onClick={() => onActionSelect(action)}
          >
            {/* Action icon */}
            <div className="w-8 h-8 mr-3  flex-shrink-0 flex items-center justify-center bg-gray-100 rounded-full"
                 style={{ marginRight: '10px' }}
            >
              {getActionIcon(action.id)}
            </div>
            
            {/* Action name and description */}
            <div className="flex-grow">
              <Text size="sm" fw={isSelected ? 700 : 500} lineClamp={1}>
                {action.name}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={1}>
                {action.description}
              </Text>
            </div>
          </div>
          <Divider my={0} opacity={0.2} />
        </div>
      </Tooltip>
    );
  };
  
  return (
    <div className="h-full flex flex-col px-2">
      {/* Search input */}
      <TextInput
        placeholder="Search actions..."
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        leftSection={<IconSearch size={20} />}
        rightSection={
          search ? (
            <ActionIcon onClick={() => setSearch('')} size="sm" variant="subtle">
              <IconX size={16} />
            </ActionIcon>
          ) : null
        }
        style={{ flex: 0 }}
        size="sm"
        mb="md"
      />
      
      {/* Actions list */}
      <div className="flex-grow overflow-hidden rounded-md">
        <ScrollArea className="h-full" viewportRef={scrollAreaRef}>
          {isLoading ? (
            <Center style={{ height: '100%' }}>
              <Loader size="md" />
            </Center>
          ) : isError ? (
            <Alert icon={<IconAlertCircle size={20} />} title="Error" color="red">
              Failed to load actions
            </Alert>
          ) : filteredActions.length === 0 ? (
            <Center style={{ height: '100%' }}>
              <Box className="text-center">
                <IconInfoCircle size={24} className="text-gray-400 mx-auto mb-2" />
                <Text c="dimmed">No actions found</Text>
              </Box>
            </Center>
          ) : (
            <div>
              {filteredActions.map((action) => (
                <div key={action.id}>
                  {renderActionRow(action, selectedAction?.id === action.id)}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

export default ActionsList; 